<script setup>
import { ref, computed, onBeforeMount, onBeforeUnmount, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'

import { TriangleAlert } from 'lucide-vue-next'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { PlusCircle, Folders, Globe, Radar, Package, Satellite, LayoutGrid } from 'lucide-vue-next'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select'
import { FormCreateOrder, FormCreatePlanetVarLayer, FormCreateRadar, FormCreateCollectedData, FormStatic, FormCreateTasking, FormCreateLulc } from '@/components/layers'
import { FeatureSelection } from '@/components/feature-selection'

const emit = defineEmits(['formSubmitted'])

const store = useStore()
const router = useRouter()
const route = useRoute()
const orgId = computed(() => parseInt(route.params.org_id))
const org = computed(() => store.getters['org/getCurrent'])
const isOrgLoading = ref(true)
const hasPlanetscopeAccess = computed(() => store.getters['geo/getIsPlanetscopeAccess'])

// Default features configuration
const DEFAULT_FEATURES = {
  static: true,
  collected_data: true,
  radar: false,
  planet_vars: false,
  planet_order: false,
  planet_tasking: false,
  lulc: true
}

onBeforeMount(async () => {
  isOrgLoading.value = true
  await store.dispatch('org/fetchOrg', orgId.value)
  isOrgLoading.value = false
})

watch(orgId, async (newOrgId) => {
  isOrgLoading.value = true
  await store.dispatch('org/fetchOrg', newOrgId)
  isOrgLoading.value = false
})

const productGroups = [
  {
    label: 'Organization Data',
    products: [
      {
        label: 'Static',
        value: 'static',
        aoiRequirement: 'not_required',
        component: FormStatic,
        icon: Folders,
        color: '#FF9900',
        type: 5
      },
      {
        label: 'Project Data',
        value: 'collectedData',
        aoiRequirement: 'not_required',
        component: FormCreateCollectedData,
        icon: Folders,
        color: '#FF9900',
        type: 4
      },
      {
        label: 'Classification',
        value: 'lulc',
        aoiRequirement: 'required',
        component: FormCreateLulc,
        icon: LayoutGrid,
        color: '#7C3AED',
        type: null
      }
    ]
  },
  {
    label: 'Planet',
    products: [
      {
        label: 'Order Scene',
        value: 'order-scene',
        aoiRequirement: 'required',
        component: FormCreateOrder,
        icon: Package,
        color: '#4CAF50',
        type: 1
      },
      {
        label: 'Planetary Variables',
        value: 'planetVars',
        aoiRequirement: 'required',
        component: FormCreatePlanetVarLayer,
        icon: Globe,
        color: '#7ED321',
        type: 2
      },
      {
        label: 'Tasking (SkySat)',
        value: 'tasking',
        aoiRequirement: 'required',
        component: FormCreateTasking,
        icon: Satellite,
        color: '#9C27B0',
        type: 6
      }
    ]
  },
  {
    label: 'Protected Seas',
    products: [
      {
        label: 'Radar',
        value: 'radar',
        aoiRequirement: 'not_required',
        component: FormCreateRadar,
        icon: Radar,
        color: '#1A73E8',
        type: 3
      },
    ]
  }
]

// Mapping between product values and their corresponding feature flags
const PRODUCT_FEATURE_MAPPING = {
  'static': 'static',
  'collectedData': 'collected_data',
  'radar': 'radar',
  'planetVars': 'planet_vars',
  'order-scene': 'planet_order',
  'tasking': 'planet_tasking',
  'lulc': 'lulc'
}

const isProductEnabled = (product, features, groupLabel) => {
  // Check if product has a feature flag requirement
  const featureFlag = PRODUCT_FEATURE_MAPPING[product.value]
  if (featureFlag && !features[featureFlag]) {
    return false
  }

  // Check Planet-specific access
  if (groupLabel === 'Planet' && !hasPlanetscopeAccess.value) {
    return false
  }

  return true
}

const allProducts = computed(() => {
  // Merge: any flag the backend doesn't send falls back to DEFAULT_FEATURES,
  // so new flags (e.g. lulc) show up for orgs created before the flag existed.
  const features = { ...DEFAULT_FEATURES, ...(org.value?.features || {}) }

  return productGroups.flatMap(group => {
    const products = group.products.filter(product =>
      isProductEnabled(product, features, group.label)
    )
    return products.map(product => ({...product, group: group.label}))
  })
})

const visibleProductGroups = computed(() => {
  const features = { ...DEFAULT_FEATURES, ...(org.value?.features || {}) }

  return productGroups.filter(group => {
    const hasEnabledProducts = group.products.some(product =>
      isProductEnabled(product, features, group.label)
    )
    return hasEnabledProducts
  })
})

const availableProductValues = computed(() => {
  return new Set(allProducts.value.map(p => p.value))
})

const selectedOrganizationId = computed(() => {
  return org.value?.organization_id?.toString() || ''
})
const selectedProductValue = ref(allProducts.value[0]?.value)
const selectedProduct = computed(() => {
  return allProducts.value.find(product => product.value === selectedProductValue.value)
})

// Map assignment (demo): which Map the new layer lands in.
const maps = computed(() => store.getters['maps/getMaps'](orgId.value))
const defaultMapId = computed(() => store.getters['maps/getDefaultMap'](orgId.value)?.id)
const selectedMapId = ref(
  (typeof route.query.map_id === 'string' ? route.query.map_id : null) || defaultMapId.value
)
watch([maps, defaultMapId], () => {
  if (!selectedMapId.value) selectedMapId.value = defaultMapId.value
})

// Focus the target map's tile so drawing (and other global map interactions)
// happen on the map this layer is being created for, not the first tile.
watch(selectedMapId, (mapId) => {
  store.dispatch('split/setFocusedTile', { mapId })
}, { immediate: true })

onBeforeUnmount(() => {
  store.dispatch('split/setFocusedTile', { mapId: null })
})

const layerName = ref('')
const selectedGeometry = ref(null)
const isOpen = ref(false)
const showOptionalAOI = ref(false)

const onFormSubmitted = async (newLayer) => {
  emit('formSubmitted', newLayer)
  // Some products (LULC) create multiple layers and refresh the store themselves,
  // so they emit null — skip the per-layer add and just navigate.
  if (newLayer) {
    await store.dispatch('layers/addOrgLayer', { orgId: orgId.value, layer: newLayer })
    // The backend already attached the new layer to the Default Map; refetch so
    // state reflects that, then move it only if the user picked a different map.
    await store.dispatch('maps/fetchMaps', { orgId: orgId.value })
    const mapId = selectedMapId.value || defaultMapId.value
    if (mapId && newLayer.code && mapId !== defaultMapId.value) {
      store.dispatch('maps/moveLayerToMap', { orgId: orgId.value, layerCode: newLayer.code, mapId })
    }
  }
  router.push({ name: 'OrgLayers', params: { org_id: orgId.value } })
}
</script>

<template>
<div class="p-2">
  <div v-if="isOrgLoading" class="w-full p-4 flex justify-center">
    <p>Loading organization data...</p>
  </div>
  <div v-else class="w-full overflow-x-auto">
    <div class="mb-3">
      <Label class="mb-1">Name</Label>
      <Input v-model.trim="layerName" placeholder="Layer Name" />
      <p v-if="layerName === ''" class="text-xs text-red-500 mt-1">Name is required</p>
    </div>

    <div class="mb-5">
      <Label class="mb-1">Layer type</Label>
      <Select v-model="selectedProductValue">
        <SelectTrigger>
          <SelectValue placeholder="Select layer type">
            <span v-if="selectedProduct" class="flex items-center">
              <component :is="selectedProduct.icon" class="w-4 h-4 mr-2" :style="{ color: selectedProduct.color }" />
              {{ selectedProduct.label }}
            </span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent class="max-h-[300px]">
          <SelectGroup v-for="group in visibleProductGroups" :key="group.label">
            <SelectLabel>{{ group.label }}</SelectLabel>
            <SelectItem
              v-for="product in group.products"
              :key="product.value"
              :value="product.value"
              :disabled="(group.label === 'Planet' && !hasPlanetscopeAccess) || !availableProductValues.has(product.value)"
              v-show="availableProductValues.has(product.value)"
            >
              <div class="flex items-center">
                <component :is="product.icon" class="w-4 h-4 mr-2" :style="{ color: product.color }" />
                {{ product.label }}
                <span
                  v-if="product.value === 'lulc'"
                  class="ml-2 text-[10px] uppercase tracking-wide font-medium px-1.5 py-0.5 rounded bg-primary text-primary-foreground"
                >
                  Beta
                </span>
                <span v-if="group.label === 'Planet' && !hasPlanetscopeAccess" class="ml-2 text-xs text-gray-400">(Access Required)</span>
              </div>
            </SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>

    <div v-if="selectedProduct?.aoiRequirement === 'required' || (selectedProduct?.aoiRequirement === 'optional' && showOptionalAOI)" :key="`feature-${selectedProductValue}`" class="mt-4">
      <Label class="mb-1">AOI <span class="text-gray-500">(Area of Interest)</span></Label>
      <FeatureSelection v-model:selectedGeometry="selectedGeometry" :allowed-draw-modes="['circle', 'polygon']" />

      <div class="mt-4" v-if="selectedProduct?.aoiRequirement === 'required' && (!selectedOrganizationId || !selectedGeometry)" :key="`warning-${selectedProductValue}`">
        <p class="flex items-center text-amber-500 bg-yellow-50 border-amber-100 border rounded p-2">
          <TriangleAlert class="w-5 h-5 mr-2" />
          Select AOI to continue
        </p>
      </div>
    </div>

    <div v-if="selectedProduct?.aoiRequirement === 'optional' && !showOptionalAOI" class="mt-4">
      <Button
        variant="secondary"
        size="sm"
        @click="showOptionalAOI = true"
      >
        <PlusCircle class="w-4 h-4 mr-2" />
        AOI (Area of Interest)
      </Button>
    </div>

    <div
      class="mt-4"
      v-if="selectedProduct?.component && (
        selectedProduct?.aoiRequirement === 'not_required' ||
        (selectedProduct?.aoiRequirement === 'required' && selectedGeometry) ||
        (selectedProduct?.aoiRequirement === 'optional')
      )"
      :key="`component-${selectedProductValue}-${!!selectedGeometry}`"
    >
      <component
        :is="selectedProduct.component"
        :feature="selectedGeometry"
        :orgId="selectedOrganizationId"
        :name="layerName"
        @formSubmitted="onFormSubmitted($event)"
      />
    </div>
  </div>
</div>
</template>
